import type { PowerNow } from "@energyplatform/client";

// The house view (prototype "Moje miejsca", owner): power now between the grid, the house, PV, the home battery and
// the largest loads, from the balance the host calculates. Nothing here adds points into a balance of its own.

/** A flow of the house view: kW (never negative) and which way it goes; null kW when it is not measured. */
export interface HouseFlow {
  kw: number | null;
  /** "in": into the house (from the grid, from PV, from the battery); "out": out of it (export, charging). */
  direction: "in" | "out" | "idle";
}

export interface HouseLoad {
  pointId: string;
  name: string;
  kw: number;
}

export interface HouseState {
  grid: HouseFlow;
  pv: HouseFlow | null;
  battery: HouseFlow | null;
  /** What the house uses now (kW), from the balance. */
  use: number | null;
  /** The largest measured loads, at most `maxLoads`. */
  loads: HouseLoad[];
  /** Use no load measures: the rest of the house. */
  rest: number | null;
}

/** Below this a flow is drawn as standing still (meters' noise, standby). */
export const IDLE_KW = 0.02;

const flow = (kw: number | null, inward: boolean): HouseFlow => ({
  kw: kw === null ? null : Math.abs(kw),
  direction:
    kw === null || Math.abs(kw) < IDLE_KW
      ? "idle"
      : inward === kw > 0
        ? "in"
        : "out",
});

/**
 * The house now: grid import is a flow in and export out; PV only flows in; a home battery measured as a storage point
 * flows out while charging (its meter draws, positive power) and in while discharging. The largest outlet loads are
 * named; the rest of the use is what they do not cover.
 */
export function houseState(now: PowerNow, maxLoads = 4): HouseState | null {
  if (!now.hasBoundary) {
    return null;
  }
  const storage = now.points.filter(
    (p) => p.role === "storage" && p.power !== null,
  );
  const batteryKw =
    storage.length === 0
      ? null
      : storage.reduce((a, p) => a + (p.power ?? 0), 0);
  const outlets = new Set(now.outlets);
  const measured = now.points
    .filter(
      (p) =>
        outlets.has(p.pointId) &&
        p.role !== "storage" &&
        p.power !== null &&
        p.power > IDLE_KW,
    )
    .map((p) => ({ pointId: p.pointId, name: p.name, kw: p.power! }))
    .sort((a, b) => b.kw - a.kw);
  const use = now.consumption;
  const covered = measured.reduce((a, l) => a + l.kw, 0);
  return {
    grid: flow(now.grid, true),
    pv:
      now.production === null
        ? null
        : { ...flow(Math.abs(now.production), true) },
    // A charging battery draws from the house (out of it), a discharging one feeds it.
    battery: batteryKw === null ? null : flow(batteryKw, false),
    use,
    loads: measured.slice(0, maxLoads),
    rest: use === null ? null : Math.max(0, use - covered),
  };
}

/** Line width for a flow: thin when small, at most `max` px; proportional to the largest flow of the view. */
export function flowWidth(
  kw: number | null,
  largest: number,
  max = 10,
): number {
  if (kw === null || kw < IDLE_KW || largest <= 0) {
    return 1.5;
  }
  return Math.max(2, Math.min(max, (kw / largest) * max));
}
